import React, { useEffect, useRef, useState } from 'react';

import { useUserSearch } from '@/Order/hooks/useUserSearch';
import { useOrderCreateStore } from '@/Order/store/orderCreateStore';

const CustomerInformationSection: React.FC = () => {
  const {
    userList,
    isLoading,
    hasResults,
    searchKeyword,
    isDropdownOpen,
    selectedUser,
    formData,
    handleSearchChange,
    handleUserSelect,
    closeDropdown,
    handleFormChange,
  } = useUserSearch();

  const {
    receiverInfo,
    updateReceiverInfo,
    setSelectedUser,
    setReceiverInfoFromUser,
  } = useOrderCreateStore();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // 디바운싱을 위한 로컬 상태
  const [localSearchKeyword, setLocalSearchKeyword] = useState<string>('');

  // 디바운싱 처리
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearchChange(localSearchKeyword);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchKeyword, handleSearchChange]);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, closeDropdown]);

  // 유저 선택 시 로컬 검색어도 업데이트
  useEffect(() => {
    if (selectedUser) {
      setLocalSearchKeyword(selectedUser.email);
      // Zustand store에도 선택된 사용자 저장
      setSelectedUser(selectedUser);
      setReceiverInfoFromUser(selectedUser);
    }
  }, [selectedUser, setSelectedUser, setReceiverInfoFromUser]);

  // receiverInfo 필드 변경 핸들러
  const handleReceiverInfoChange = (
    field: keyof typeof receiverInfo,
    value: string,
  ) => {
    updateReceiverInfo(field, value);
  };

  return (
    <div className="flex flex-col rounded border border-gray-200 bg-white shadow-sm">
      <form className="m-5">
        {/* Contact Information */}
        <div className="mb-8">
          <h2 className="text-base font-medium text-[#1D1D1D]">
            Contact Information
          </h2>
          <div className="relative mt-3" ref={searchContainerRef}>
            <div className="w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm">
              <input
                type="text"
                placeholder="Search User"
                value={localSearchKeyword}
                onChange={(e) => setLocalSearchKeyword(e.target.value)}
                className="h-full w-full focus:outline-none"
              />
            </div>

            {/* 드롭다운 */}
            {isDropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute left-0 top-full z-50 mt-1 max-h-[400px] w-full overflow-y-auto rounded border border-[#DDDDDD] bg-white shadow-lg"
              >
                {isLoading && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    검색 중...
                  </div>
                )}

                {!isLoading &&
                  !hasResults &&
                  searchKeyword.trim().length > 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      검색 결과가 없습니다.
                    </div>
                  )}

                {!isLoading && hasResults && (
                  <ul>
                    {userList.map((user) => (
                      <li key={user.uuid}>
                        <button
                          type="button"
                          onClick={() => handleUserSelect(user)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-[#1D1D1D]">
                              {user.email}
                            </span>
                            <span className="text-xs text-[#666666]">
                              {user.companyName}
                            </span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="mt-4">
            <label htmlFor="company-name" className="text-sm text-gray-600">
              Company Name
            </label>
            <input
              type="text"
              id="company-name"
              value={formData.companyName}
              onChange={(e) => handleFormChange('companyName', e.target.value)}
              className="mt-2 w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm text-gray-800 outline-none"
            />
          </div>
          <div className="mt-4">
            <label
              htmlFor="representative-name"
              className="text-sm text-gray-600"
            >
              Representative Name
            </label>
            <input
              type="text"
              id="representative-name"
              value={formData.representativeName}
              onChange={(e) =>
                handleFormChange('representativeName', e.target.value)
              }
              className="mt-2 w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm text-gray-800 outline-none"
            />
          </div>
          <div className="mt-4">
            <label
              htmlFor="business-registration-number"
              className="text-sm text-gray-600"
            >
              Business Registration Number
            </label>
            <input
              type="text"
              id="business-registration-number"
              value={formData.businessRegistrationNumber}
              onChange={(e) =>
                handleFormChange('businessRegistrationNumber', e.target.value)
              }
              className="mt-2 w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm text-gray-800 outline-none"
            />
          </div>
          <div className="mt-4">
            <label htmlFor="email" className="text-sm text-gray-600">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              className="mt-2 w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm text-gray-800 outline-none"
            />
          </div>
          <div className="mt-4">
            <label htmlFor="phone-number" className="text-sm text-gray-600">
              Phone Number
            </label>
            <input
              type="text"
              id="phone-number"
              value={formData.phoneNumber}
              onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
              className="mt-2 w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm text-gray-800 outline-none"
            />
          </div>
        </div>

        {/* Shipping Information */}
        <div>
          <h2 className="text-base font-medium text-[#1D1D1D]">
            Shipping Information
          </h2>
          <div className="mt-4">
            <label htmlFor="receiver-name" className="text-sm text-gray-600">
              Receiver Name
            </label>
            <input
              type="text"
              id="receiver-name"
              value={receiverInfo.receiverName}
              onChange={(e) =>
                handleReceiverInfoChange('receiverName', e.target.value)
              }
              className="mt-2 w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm text-gray-800 outline-none"
            />
          </div>
          <div className="mt-4">
            <label htmlFor="postal-code" className="text-sm text-gray-600">
              Postal Code
            </label>
            <input
              type="text"
              id="postal-code"
              value={receiverInfo.postalCode || ''}
              onChange={(e) =>
                handleReceiverInfoChange('postalCode', e.target.value)
              }
              className="mt-2 w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm text-gray-800 outline-none"
            />
          </div>
          <div className="mt-4">
            <label htmlFor="address-detail" className="text-sm text-gray-600">
              Address Detail
            </label>
            <input
              type="text"
              id="address-detail"
              value={receiverInfo.addressDetail}
              onChange={(e) =>
                handleReceiverInfoChange('addressDetail', e.target.value)
              }
              className="mt-2 w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm text-gray-800 outline-none"
            />
          </div>
          <div className="mt-4">
            <label htmlFor="receiver-phone" className="text-sm text-gray-600">
              Phone Number
            </label>
            <input
              type="text"
              id="receiver-phone"
              value={receiverInfo.phoneNumber}
              onChange={(e) =>
                handleReceiverInfoChange('phoneNumber', e.target.value)
              }
              className="mt-2 w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm text-gray-800 outline-none"
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default CustomerInformationSection;
